import {Observable} from 'rxjs';
import {switchMap, map, takeWhile} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ApiConfig} from '~shared/constants/api.config';
import {HttpCrudService} from '~shared/services/http-crud.service';

import {IAddressDB} from '../models/address.model';
import {Address} from '~app/modules/checkout/models';
import {AccountInfoService} from '~core/modules/identity/services/account-info.service';
import {
  AddressMetroXHttpService
} from '~app/modules/account/modules/address-book/services/address-metro-x-http.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_ACCOUNT_INFO_V2, FF_SL_NG_ADDRESS_BOOK } from '~app/modules/authorization/constants/authorization.constant';
import { AccountType } from '~shared/models';

@Injectable({
  providedIn: 'root',
})
export class AddressBookHttpService extends HttpCrudService {
  public get addresses$(): Observable<IAddressDB[]> {
    return this.data$;
  }

  private apiEndpoint$ = this.accountInfoService.isEntrepreneur().pipe(
    map((isEntrepreneur) => {
      const apiEndpoint = isEntrepreneur ? '/entrepreneur/addresses' : '/consumers/addresses';
      return ApiConfig.SVC_USER_ACCOUNT_API_URL().concat(apiEndpoint);
    }),
  );

  accountType$ = this.accountInfoService.accountType$;
  isAccountInfoV2APIEnabled$ =
    this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2);
  isBuyerAddressBookAPIEnabled$ =
    this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(FF_SL_NG_ADDRESS_BOOK);

  constructor(
    http: HttpClient,
    private accountInfoService: AccountInfoService,
    private addressMetroXHttpService: AddressMetroXHttpService,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService
  ) {
    super(http);
  }

  public read(): Observable<IAddressDB[]> {
    return this.accountType$.pipe(
      takeWhile((accountType) => accountType !== null),
      switchMap((accountType) => {
        if (accountType === AccountType.EMPLOYEE || accountType === AccountType.EMPLOYEE_AS_CUSTOMER) {
          return this.addressMetroXHttpService.getInImpersonationSession();
        }
        return this.isBuyerAddressBookAPIEnabled$.pipe(
          switchMap((isEnabled) => {
            if (isEnabled) {
              return this.addressMetroXHttpService.getWithBuyerAccountAPIEndpoint()
            } else {
              return this.addressMetroXHttpService.get();
            }
          })
        );
      })
    );
  }

  public delete(id: string): Observable<void> {
    return this.apiEndpoint$.pipe(
      switchMap((apiEndpoint) => {
        const url = `${apiEndpoint}/${id}`;
        return this.request({
          url,
          reload: true,
          method: 'delete',
        });
      }),
    );
  }

  public create(address: IAddressDB): Observable<IAddressDB> {
    return this.addressMetroXHttpService.create(address);
  }

  public update(address: IAddressDB): Observable<IAddressDB> {
    return this.addressMetroXHttpService.update(address);
  }

  public batchCreate(addresses: Address[]): Observable<Address[]> {
    return this.addressMetroXHttpService.createBatch(addresses);
  }
}
